import {useState} from 'react';

import {CallControls} from './components/CallControls';
import {VideoRenderer} from './components/VideoRenderer';
import {useMediaControls} from './hooks/useMediaControls';
import {usePermissionDeviceRestart} from './hooks/usePermissionDeviceRestart';
import {useVidyoConnection} from './hooks/useVidyoConnection';
import {DebugBanner} from './DebugBanner';
import {EndCallBanner} from './EndCallBanner';
import {PermissionsBanner} from './PermissionsBanner';
import Settings from './Settings';
import {SelectOption} from './types';
import {useDebug} from './useDebug';
import {useMediaPermissions} from './useMediaPermissions';

import './styles.css';
import styles from './videoCall.module.scss';

type VideoCallWidgetProps = {
    currentMeetingId: string;
    user: {
        firstName: string;
        id: number;
        lastName?: string;
    };
    onClose?: () => void;
    isForceLoadingState?: boolean;
    newWindowRef?: React.MutableRefObject<Window | null>;
    onDisconnect?: () => void;
    initCameraList?: SelectOption[];
};

export const VideoCallWidget = ({
    currentMeetingId: currentCallMeetingId,
    user,
    onClose,
    onDisconnect,
    isForceLoadingState = false,
    initCameraList = [],
}: VideoCallWidgetProps) => {
    const {isDebug, debugStatus, setDebugStatus} = useDebug();
    const {permissions} = useMediaPermissions();
    const [showSettings, setShowSettings] = useState(false);

    const mediaControls = useMediaControls();

    usePermissionDeviceRestart({
        onCameraRestart: mediaControls.restartCamera,
        onMicRestart: mediaControls.restartMicrophone,
    });

    const handleClose = () => {
        onClose?.();
        setTimeout(() => {
            window?.close();
        }, 1000);
    };

    const connection = useVidyoConnection({
        currentCallMeetingId,
        user,
        isForceLoadingState,
        initCameraList,
        onClose: handleClose,
        onDisconnect,
        onBgNoiseInit: mediaControls.onBgNoiseInit,
        isDebug,
        setDebugStatus,
    });

    return (
        <div
            id="videoCallWidget"
            className={styles.root}
            style={{
                visibility: 'visible',
                display: 'block',
                top: 0,
                left: 0,
                overflow: 'hidden',
                zIndex: 1000,
                pointerEvents: 'auto',
                width: '100%',
                height: '100%',
                minHeight: '100%',
                minWidth: '100%',
                backgroundColor: 'black',
            }}
        >
            {showSettings && (
                <Settings
                    onClose={() => setShowSettings(false)}
                    micList={connection.micList}
                    speakerList={connection.speakerList}
                    cameraList={connection.cameraList}
                    curMic={connection.curMic}
                    curSpeaker={connection.curSpeaker}
                    curCamera={connection.curCamera}
                    setCurMic={connection.handleMicChange}
                    setCurSpeaker={connection.handleSpeakerChange}
                    setCurCamera={connection.handleCameraChange}
                />
            )}
            <div id="vidyoConnector" className={styles.vidyoConnector}>
                <PermissionsBanner cameraPermission={permissions.camera} micPermission={permissions.microphone} />
                <DebugBanner isDebug={isDebug} debugStatus={debugStatus} />
                <EndCallBanner reason={connection.callEndReason} onClose={handleClose} />
                <VideoRenderer isLoading={connection.isLoading} isConnected={connection.isConnected} />
                <CallControls
                    currentMeetingId={currentCallMeetingId}
                    isConnected={connection.isConnected}
                    cameraBlocked={permissions.camera === 'denied'}
                    micBlocked={permissions.microphone === 'denied'}
                    isCameraOn={mediaControls.isCameraOn}
                    isMicrophoneOn={mediaControls.isMicrophoneOn}
                    isSpeakerOn={mediaControls.isSpeakerOn}
                    bgNoiseOn={mediaControls.bgNoiseOn}
                    curCamera={connection.curCamera}
                    curMic={connection.curMic}
                    curSpeaker={connection.curSpeaker}
                    cameraList={connection.cameraList}
                    micList={connection.micList}
                    speakerList={connection.speakerList}
                    onEndCall={connection.handleClose}
                    onCameraToggle={mediaControls.handleCameraPrivacy}
                    onMicToggle={mediaControls.handleMicrophonePrivacy}
                    onSpeakerToggle={mediaControls.handleSpeakerPrivacy}
                    onBgNoiseToggle={mediaControls.handleBackgroundNoise}
                    onScreenShare={mediaControls.handleScreenShare}
                    onOpenSettings={() => setShowSettings(true)}
                    onCameraChange={connection.handleCameraChange}
                    onMicChange={connection.handleMicChange}
                    onSpeakerChange={connection.handleSpeakerChange}
                />
            </div>
        </div>
    );
};
